// frontend/src/SettingsPage.js
import React, { useState, useEffect, useCallback, useMemo, useId } from 'react';
import axios from 'axios';
import SettingsSidebar from './SettingsSidebar';
import './SettingsPage.css'; // Import the CSS file
import * as XLSX from 'xlsx';

const SettingsPage = ({ handleLogout, theme, setTheme })=> {
    // --- Default Settings and Load Logic ---
    const defaultSettings = useMemo(() => ({
        llmProvider: 'OpenAI',
        model: 'gpt-4o-mini',
        temperature: 0.5,
        maxTokens: 2500,
        url: 'https://api.openai.com/v1/chat/completions', // Default URL might get overwritten
    }), []);

    // --- Provider/Model Map ---
    // (Kept the same as provided previously)
    const providerModelMap = useMemo(() => ({
         OpenAI: [
             { name: 'gpt-4o-mini', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '16000' },
             { name: 'gpt-4o', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '128000'  },
             { name: 'o3-mini', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '128000'  },
             { name: 'o4-mini', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '128000'  },
             { name: 'o1-mini', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '128000'  },
         ],
         Anthropic: [
             { name: 'claude-3-5-sonnet-latest', url: 'https://api.anthropic.com/v1/messages', model_max_tokens: '8192'  },
             { name: 'claude-3-opus-latest', url: 'https://api.anthropic.com/v1/messages', model_max_tokens: '4096'  },
             { name: 'claude-3-sonnet-20240229', url: 'https://api.anthropic.com/v1/messages', model_max_tokens: '4096'  },
             { name: 'claude-3-haiku-20240307', url: 'https://api.anthropic.com/v1/messages', model_max_tokens: '4096'  },
         ],
         OpenSource: [
             { name: 'meta-llama/llama-4-scout-17b-16e-instruct', url: '', model_max_tokens: '131072'  },
             { name: 'deepseek-r1-distill-llama-70b', url: '', model_max_tokens: '128000'  },
             { name: 'llama-3.1-8b-instant', url: '', model_max_tokens: '128000'  },
             { name: 'meta-llama/llama-4-maverick-17b-128e-instruct', url: '', model_max_tokens: '131072'  },
         ],
         Grok: [
            { name: 'grok-3-mini', url: '', model_max_tokens: '131072'  },
            { name: 'grok-4', url: '', model_max_tokens: '131072'  },
            { name: 'grok-3', url: '', model_max_tokens: '131072'  },
        ],
         Local: [
             { name: 'llama3:latest', url: 'http://localhost:11434/api/chat', model_max_tokens: '8192' },
         ]
     }), []);

    // (Function loadSettingsFromLocalStorage kept the same as provided previously)
    const loadSettingsFromLocalStorage = useCallback(() => {
        const savedSettings = localStorage.getItem('llmSettings');
        if (savedSettings) {
            try {
                const parsed = JSON.parse(savedSettings);
                // Validate provider
                if (!parsed.llmProvider || !providerModelMap[parsed.llmProvider]) {
                     console.warn("Invalid provider in saved settings, reverting to default.");
                     return defaultSettings;
                }
                const providerModels = providerModelMap[parsed.llmProvider];
                // Find selected model within the valid provider's models
                const selectedModel = providerModels?.find(m => m.name === parsed.model);

                if (selectedModel) {
                    // Update URL from the found model details
                    parsed.url = selectedModel.url;
                } else if (providerModels && providerModels[0]) {
                    // If saved model doesn't exist for the provider, use the first available model
                    console.warn(`Saved model '${parsed.model}' not found for provider '${parsed.llmProvider}', using default '${providerModels[0].name}'.`);
                    parsed.model = providerModels[0].name;
                    parsed.url = providerModels[0].url;
                } else {
                    // If provider has no models listed (or provider was invalid earlier), revert fully
                    console.warn(`No models found for provider '${parsed.llmProvider}', reverting to default settings.`);
                    return defaultSettings;
                }

                // Validate and clamp temperature
                parsed.temperature = typeof parsed.temperature === 'number' && !isNaN(parsed.temperature)
                                      ? Math.max(0, Math.min(2, parsed.temperature))
                                      : defaultSettings.temperature;

                // Validate and clamp maxTokens based on the potentially updated model
                parsed.maxTokens = typeof parsed.maxTokens === 'number' && !isNaN(parsed.maxTokens)
                                      ? Math.max(1, parsed.maxTokens) // Ensure at least 1
                                      : defaultSettings.maxTokens;

                const finalSelectedModel = providerModels?.find(m => m.name === parsed.model); // Re-check model details
                const modelMax = finalSelectedModel ? parseInt(finalSelectedModel.model_max_tokens, 10) : null;
                if (modelMax && !isNaN(modelMax)) {
                    parsed.maxTokens = Math.min(parsed.maxTokens, modelMax); // Clamp to model max
                }

                // Merge parsed (and validated) settings with defaults
                return { ...defaultSettings, ...parsed };
            } catch (e) {
                 console.error("Error parsing settings from localStorage:", e);
                 return defaultSettings; // Fallback to defaults on parse error
            }
        }
        return defaultSettings; // Return defaults if no settings saved yet
    }, [defaultSettings, providerModelMap]);

    // --- State Definitions ---
    const [settings, setSettings] = useState(loadSettingsFromLocalStorage);
    const [prompts, setPrompts] = useState([]);
    const [selectedPrompt, setSelectedPrompt] = useState(null);
    const [activeTab, setActiveTab] = useState('Main');
    const [pipelinePrompts, setPipelinePrompts] = useState([]);
    const [pipelines, setPipelines] = useState([]);
    const [selectedPipeline, setSelectedPipeline] = useState(null);
    const [maxTokenValue, setMaxTokenValue] = useState(() => {
         const currentProviderModels = providerModelMap[settings.llmProvider];
         const currentModel = currentProviderModels?.find(model => model.name === settings.model);
         return currentModel ? parseInt(currentModel.model_max_tokens, 10) || 16000 : 16000; // Default fallback
    });
    const [promptsLoading, setPromptsLoading] = useState(false);
    const [pipelinesLoading, setPipelinesLoading] = useState(false);
    const [apiError, setApiError] = useState(null);
    // --- NEW: Loading state for saving settings ---
    const [isSaving, setIsSaving] = useState(false);

    // --- API Error Handling (kept the same) ---
     const handleApiError = useCallback((error, context) => {
        console.error(`Error ${context}:`, error);
        let errorMessage = `An error occurred while ${context}.`;
        if (error.response) {
            // Use error message from backend response if available
            errorMessage = error.response.data?.error || error.response.data?.message || `Server error ${error.response.status} during ${context}.`;
            // Handle authentication errors specifically
            if (error.response.status === 401 && handleLogout) {
                console.log("Unauthorized error detected, attempting logout.");
                setApiError("Session expired or invalid. Please log in again.");
                setTimeout(() => handleLogout(), 1500); // Logout after delay
                return; // Stop further processing
            }
        } else if (error.request) {
            errorMessage = `No response received while ${context}. Check network/server.`;
        } else {
            errorMessage = `Error setting up request for ${context}: ${error.message}`;
        }
        setApiError(errorMessage);
        // Automatically clear the error message after a delay
        setTimeout(() => setApiError(null), 7000); // 7 seconds
    }, [handleLogout]);

    // --- Data Fetching (kept the same) ---
    const refreshPrompts = useCallback(() => {
        setPromptsLoading(true);
        setApiError(null);
        axios.get('/api/prompts', { withCredentials: true })
            .then(response => {
                setPrompts(Array.isArray(response.data) ? response.data : []);
            })
            .catch(error => handleApiError(error, 'fetching prompts'))
            .finally(() => setPromptsLoading(false));
    }, [handleApiError]);

    const getModelDetails = (provider, modelName, providerModelMap) => {
        if (!provider || !modelName || !providerModelMap[provider]) {
            return null;
        }
        return providerModelMap[provider].find(m => m.name === modelName) || null;
    };


    const refreshPipelines = useCallback(() => {
        setPipelinesLoading(true);
        setApiError(null);
        axios.get('/api/prompt-pipelines', { withCredentials: true })
            .then(response => {
                // Backend now returns { id, name, username } for each pipeline
                setPipelines(Array.isArray(response.data) ? response.data : []);
                console.log("Fetched pipelines (with username):", response.data);
            })
            .catch(error => handleApiError(error, 'fetching pipelines list'))
            .finally(() => setPipelinesLoading(false));
    }, [handleApiError]); // Ensure handleApiError is stable or included if it changes

    useEffect(() => { refreshPrompts(); refreshPipelines(); }, [refreshPrompts, refreshPipelines]);

    // --- Update max token limit based on selected model (kept the same) ---
    useEffect(() => {
        const currentProviderModels = providerModelMap[settings.llmProvider];
        const currentModel = currentProviderModels?.find(model => model.name === settings.model);
        let newMaxTokenLimit = 16000; // Default fallback
        let modelUrl = settings.url; // Keep current URL initially

        if (currentModel) {
            const modelMax = parseInt(currentModel.model_max_tokens, 10);
            if (!isNaN(modelMax) && modelMax > 0) {
                newMaxTokenLimit = modelMax;
            } else {
                console.warn(`Invalid or missing model_max_tokens for ${settings.model}, using default ${newMaxTokenLimit}.`);
            }
            modelUrl = currentModel.url; // Update URL based on selected model
        } else if (settings.llmProvider && settings.model) {
            // Handle case where model details might be missing
            console.warn(`Model details not found for ${settings.llmProvider} - ${settings.model}. Using default max token limit.`);
        }

        setMaxTokenValue(newMaxTokenLimit);

        // Update settings state only if necessary (clamp maxTokens or update URL)
        setSettings(prevSettings => {
             const needsUpdate = prevSettings.maxTokens > newMaxTokenLimit || prevSettings.url !== modelUrl;
             if (needsUpdate) {
                  return { ...prevSettings, maxTokens: Math.min(prevSettings.maxTokens, newMaxTokenLimit), url: modelUrl };
             }
             return prevSettings; // No change needed
        });
    }, [settings.llmProvider, settings.model, providerModelMap, settings.url]); // Added settings.url dependency

    // --- Event Handlers (most kept the same) ---
    const handleSettingChange = useCallback((key, value) => {
        setSettings(prevSettings => {
            let newSettings = { ...prevSettings };
            if (key === 'llmProvider') {
                const firstModel = providerModelMap[value]?.[0];
                if (firstModel) {
                    const modelMax = parseInt(firstModel.model_max_tokens, 10) || 16000;
                    newSettings = { ...newSettings, [key]: value, model: firstModel.name, url: firstModel.url, maxTokens: Math.min(prevSettings.maxTokens, modelMax) };
                } else {
                    // Handle case where selected provider might have no models listed (unlikely with map)
                    newSettings = { ...newSettings, [key]: value, model: '', url: '' };
                }
            } else if (key === 'model') {
                const providerModels = providerModelMap[newSettings.llmProvider];
                const selectedModel = providerModels?.find(m => m.name === value);
                if (selectedModel) {
                    const modelMax = parseInt(selectedModel.model_max_tokens, 10) || 16000;
                    newSettings = { ...newSettings, [key]: value, url: selectedModel.url, maxTokens: Math.min(prevSettings.maxTokens, modelMax) };
                } else {
                    // Model not found (shouldn't happen if dropdown is populated correctly)
                    newSettings = { ...newSettings, [key]: value };
                }
            } else if (key === 'temperature') {
                const numericValue = parseFloat(value);
                 if (!isNaN(numericValue)) {
                      newSettings = { ...newSettings, [key]: Math.max(0, Math.min(2, numericValue)) };
                 }
            } else if (key === 'maxTokens') {
                const numericValue = parseInt(value, 10);
                 if (!isNaN(numericValue)) {
                    // Use the state value maxTokenValue for clamping
                    const cappedValue = Math.min(numericValue, maxTokenValue || 1);
                    newSettings = { ...newSettings, [key]: Math.max(1, cappedValue) };
                 }
            } else {
                 // Handle other potential settings if added later
                 newSettings = { ...newSettings, [key]: value };
            }
            return newSettings;
        });
    }, [providerModelMap, maxTokenValue]); // Added maxTokenValue dependency

    const handleSliderChange = useCallback((key, value) => {
        handleSettingChange(key, value);
    }, [handleSettingChange]);

    // --- Prompt & Pipeline Handlers (kept the same) ---
    const handleSelectPromptChange = useCallback((event) => {
        const promptId = event.target.value;
        setSelectedPrompt(prompts.find(p => p.id.toString() === promptId) || null);
    }, [prompts]);

    const handleAddToPipeline = useCallback(() => {
        if (selectedPrompt && !pipelinePrompts.some(p => p.id === selectedPrompt.id)) {
            setPipelinePrompts(current => [...current, selectedPrompt]);
        } else if (!selectedPrompt) {
             alert("Please select a prompt from the dropdown first.");
        } else {
             alert("This prompt is already in the current pipeline.");
        }
    }, [selectedPrompt, pipelinePrompts]);

    const handleRemoveFromPipeline = useCallback((promptToRemove) => {
        setPipelinePrompts(current => current.filter(p => p.id !== promptToRemove.id));
    }, []);

    const handleSavePipeline = useCallback(async (name, promptIds) => {
        if (!name.trim()) { alert("Please enter a name for the pipeline."); return; }
        if (promptIds.length === 0) { alert("Please add at least one prompt to the current pipeline."); return; }
        setApiError(null);
        // Create new pipeline, backend associates with current user
        try {
            await axios.post('/api/prompt-pipelines', { name: name.trim(), prompts: promptIds }, { withCredentials: true });
            alert('New pipeline saved successfully!');
            setPipelinePrompts([]); 
            setSelectedPrompt(null); // Ensure prompt selection dropdown is also reset
            setSelectedPipeline(null); // Clear selected pipeline for editor
            refreshPipelines(); 
        } catch (error) {
            handleApiError(error, 'saving new pipeline');
        }
    }, [refreshPipelines, handleApiError]);

    const handleSelectPipelineChange = useCallback(async (event) => {
        const pipelineId = event.target.value;
        setApiError(null);

        if (!pipelineId) {
            setSelectedPipeline(null);
            setPipelinePrompts([]); // Clear editor list
            return;
        }

        const selectedPipelineSummary = pipelines.find(p => p.id.toString() === pipelineId);
        if (!selectedPipelineSummary) {
            console.error(`Pipeline with ID ${pipelineId} not found in local list.`);
            setSelectedPipeline(null);
            setPipelinePrompts([]);
            return;
        }
        
        // Store the summary (id, name, username) immediately for UI responsiveness
        setSelectedPipeline(selectedPipelineSummary); 
        setPipelinesLoading(true); // Indicate loading pipeline details

        try {
            // Fetch full pipeline details (which includes the 'prompts' array of IDs and username)
            const response = await axios.get(`/api/prompt-pipelines/${pipelineId}`, { withCredentials: true });
            const pipelineDetails = response.data; // Expects { id, name, prompts: [...ids], username }

            if (pipelineDetails && Array.isArray(pipelineDetails.prompts)) {
                // Map prompt IDs from details to full prompt objects from the fetched 'prompts' state
                const orderedPrompts = pipelineDetails.prompts
                    .map(id => prompts.find(p => p.id === id || p.id.toString() === id.toString())) // Handle number/string ID comparison
                    .filter(Boolean); // Filter out any prompts not found (IDs might be stale)
                
                setPipelinePrompts(orderedPrompts); // Update the editor list with full prompt objects

                // Update selectedPipeline state with full details, including the prompt objects for consistency
                // and the username from the detailed fetch.
                setSelectedPipeline({
                    id: pipelineDetails.id,
                    name: pipelineDetails.name,
                    username: pipelineDetails.username, // Store the owner's username
                    prompts: orderedPrompts // Store full prompt objects, not just IDs
                });
                 console.log("Selected pipeline details loaded:", {
                    id: pipelineDetails.id,
                    name: pipelineDetails.name,
                    username: pipelineDetails.username,
                    promptCount: orderedPrompts.length
                });

            } else {
                console.error('Invalid pipeline details format:', pipelineDetails);
                setPipelinePrompts([]);
                setApiError('Failed to load pipeline details: Invalid data format.');
                // Revert selectedPipeline to summary if full details fail
                setSelectedPipeline(selectedPipelineSummary); 
            }
        } catch (error) {
            handleApiError(error, `fetching pipeline details for ${selectedPipelineSummary.name}`);
            setPipelinePrompts([]);
            // Revert selectedPipeline to summary if full details fail
            setSelectedPipeline(selectedPipelineSummary); 
        } finally {
            setPipelinesLoading(false);
        }
    }, [pipelines, prompts, handleApiError]);

    const clearPipelineEditor = useCallback(() => {
        console.log("clearPipelineEditor called");
        setSelectedPipeline(null);
        setPipelinePrompts([]);
        setSelectedPrompt(null); 
    }, [setSelectedPipeline, setPipelinePrompts, setSelectedPrompt]); // Using setters which are stable

    const handleUpdatePipeline = useCallback(async (pipelineId, name, promptIds) => {
        if (!pipelineId) { alert("Cannot update pipeline: ID missing."); return; }
        if (!name.trim()) { alert("Pipeline name cannot be empty."); return; }
        if (promptIds.length === 0) { alert("Pipeline must contain at least one prompt."); return; }
        setApiError(null);

        // The backend PUT /api/prompt-pipelines/<id> handles:
        // 1. If system pipeline: Creates a new pipeline for the current user.
        // 2. If user's pipeline: Updates it.
        // 3. If another user's pipeline: Denies.
        try {
            const response = await axios.put(
                `/api/prompt-pipelines/${pipelineId}`,
                { name: name.trim(), prompts: promptIds },
                { withCredentials: true }
            );
            
            alert('Pipeline action processed successfully!'); // Generic message as backend handles new vs update
            refreshPipelines(); 

            // If a new pipeline was created (status 201), or existing updated (status 200)
            // The response.data will be the pipeline object.
            // We can use this to potentially update the selectedPipeline state
            // or simply clear the editor to reflect the list being refreshed.
            if (response.status === 201) { // Created new (from system pipeline)
                console.log("System pipeline saved as new user pipeline:", response.data);
                // Optionally, select the newly created pipeline
                // setSelectedPipeline(response.data); 
                // For simplicity, let's clear and let user re-select if needed
                 clearPipelineEditor();
            } else if (response.status === 200) { // Updated existing
                console.log("User pipeline updated:", response.data);
                 // If still editing the same one, update its details in the editor
                 if (selectedPipeline && selectedPipeline.id === response.data.id) {
                    // Re-fetch the details to be absolutely sure state is consistent
                    // Or, if confident, parse response.data.prompts (list of IDs)
                    // and map them to full prompt objects for setPipelinePrompts
                    // For now, a simpler approach is to clear the editor or re-select.
                    // Let's re-select the updated pipeline to refresh editor.
                    const updatedPipelineSummary = {
                        id: response.data.id,
                        name: response.data.name,
                        username: response.data.username
                    };
                    setSelectedPipeline(updatedPipelineSummary);
                    // Trigger a re-fetch of its full details to update `pipelinePrompts`
                    const event = { target: { value: response.data.id.toString() } };
                    await handleSelectPipelineChange(event);
                 } else {
                    clearPipelineEditor();
                 }
            }
        } catch (error) {
            handleApiError(error, 'updating pipeline');
        }
    }, [refreshPipelines, handleApiError, selectedPipeline, clearPipelineEditor, handleSelectPipelineChange]);

    

    const handleUpdatePrompt = useCallback((promptId, name, text, group, llmSettings) => {
        if (!promptId) { alert('Cannot update prompt: ID missing.'); return; }
        if (!name.trim()) { alert('Prompt name cannot be empty.'); return; }
        if (!text.trim()) { alert('Prompt text cannot be empty.'); return; }
        setApiError(null);

        const payload = {
            name: name.trim(),
            text: text.trim(),
            group: group ? group.trim() : null, // Send group, or null if empty
            // Conditionally spread llmSettings if they are provided and custom settings are used
            // Assuming llmSettings contains { provider, model, temperature, max_tokens }
            // and these can be null if not using custom settings.
            provider: llmSettings.provider,
            model: llmSettings.model,
            temperature: llmSettings.temperature,
            max_tokens: llmSettings.max_tokens,
        };

        axios.put(`/api/prompts/update/${promptId}`, payload, { withCredentials: true })
        .then(response => {
            alert("Prompt updated successfully!");
            refreshPrompts(); // Refresh prompt list
            // Update the selected prompt state to reflect the change immediately
            // Backend should return the updated prompt object
            setSelectedPrompt(response.data);
        })
        .catch(error => handleApiError(error, 'updating prompt'));
    }, [refreshPrompts, handleApiError]);

    const handleCreatePrompt = useCallback((name, text, group, llmSettings) => {
        if (!name.trim()) { alert('Please enter a name for the new prompt.'); return; }
        if (!text.trim()) { alert('Please enter the text for the new prompt.'); return; }
        setApiError(null);

       const payload = {
           name: name.trim(),
           text: text.trim(),
           group: group ? group.trim() : null, // Send group, or null if empty
           // Conditionally spread llmSettings
           provider: llmSettings.provider,
           model: llmSettings.model,
           temperature: llmSettings.temperature,
           max_tokens: llmSettings.max_tokens,
       };

       axios.post('/api/prompts/create', payload, { withCredentials: true })
       .then(() => {
           alert("New prompt created successfully!");
           refreshPrompts(); // Refresh list
           // Clear the editor fields by resetting selectedPrompt
           setSelectedPrompt(null); // This will also trigger the useEffect in PromptManagementContent to clear fields
       })
       .catch(error => handleApiError(error, 'creating new prompt'));
   }, [refreshPrompts, handleApiError]);

    // --- NEW: Updated handleSaveSettings ---
    const handleSaveSettings = useCallback(async () => {
        setApiError(null); // Clear previous errors
        setIsSaving(true); // Show loading state

        // Prepare the data payload matching backend expectations
        const settingsPayload = {
            llmProvider: settings.llmProvider,
            model: settings.model,
            temperature: settings.temperature,
            maxTokens: settings.maxTokens,
        };

        console.log("Attempting to save settings:", settingsPayload);

        try {
            // Make the API call to the new backend endpoint
            const response = await axios.post('/api/user/settings', settingsPayload, {
                withCredentials: true, // Send session cookies
            });

            // If API call is successful, save to local storage (optional persistence)
            localStorage.setItem('llmSettings', JSON.stringify(settings));
            alert(response.data.message || 'Settings saved successfully!'); // Provide success feedback from backend

        } catch (error) {
            // Use the centralized error handler to display API errors
            handleApiError(error, 'saving settings');
            // Fallback alert if handleApiError doesn't cover it (e.g., network error before response)
            if (!error.response) {
                 alert('Failed to save settings. Check network connection or server status.');
            }
        } finally {
            setIsSaving(false); // Hide loading state regardless of success/failure
        }
    }, [settings, handleApiError]); // Dependencies: settings state and error handler

    // --- Clear State Handlers (kept the same) ---
    const clearSelectedPrompt = useCallback(() => {
        setSelectedPrompt(null);
    }, []);
   

    const handleDeletePrompt = useCallback(async () => {
        if (!selectedPrompt || !selectedPrompt.id) {
            alert("Please select a prompt to delete.");
            return;
        }
        // User confirmation
        if (!window.confirm(`Are you sure you want to delete the prompt "${selectedPrompt.prompt_name}"? This action cannot be undone.`)) {
            return;
        }
    
        setApiError(null); // Clear previous errors
        // Optional: Add a loading state for delete if you have one, e.g., setIsDeleting(true);
        try {
            await axios.delete(`/api/prompts/delete/${selectedPrompt.id}`, { withCredentials: true });
            alert(`Prompt "${selectedPrompt.prompt_name}" deleted successfully!`);
            refreshPrompts(); // Refresh the list of prompts from the server
            setSelectedPrompt(null); // Clear the selection and reset the form fields
                                     // This will also trigger the useEffect in PromptManagementContent
                                     // to clear its internal form state.
        } catch (error) {
            // Use the existing handleApiError to display the error
            handleApiError(error, `deleting prompt "${selectedPrompt.prompt_name}"`);
        } finally {
            // Optional: setIsDeleting(false);
        }
    }, [selectedPrompt, refreshPrompts, handleApiError, setSelectedPrompt]); // Added setSelectedPrompt to dependencies

    const handleDeletePipeline = useCallback(async () => {
        console.log("--- handleDeletePipeline (SettingsPage.js) ---"); // Log 1
    
        if (!selectedPipeline || !selectedPipeline.id) {
            alert("Please select a pipeline to delete.");
            console.warn("handleDeletePipeline: ABORT - No pipeline selected or ID missing. Current selectedPipeline:", selectedPipeline); // Log 2
            return;
        }
    
        console.log("handleDeletePipeline: Pipeline selected:", selectedPipeline); // Log 2.1: Log the full selected pipeline object
    
        // This check is important. The button should ideally not even be visible for system pipelines.
        if (selectedPipeline.username === null || selectedPipeline.username === '') {
            alert("System pipelines cannot be deleted directly. You can 'Save as New Copy' to create your own version.");
            console.warn("handleDeletePipeline: ABORT - Attempt to delete a system pipeline. Username:", selectedPipeline.username); // Log 3
            return;
        }
    
        if (!window.confirm(`Are you sure you want to delete your pipeline "${selectedPipeline.name}"? This action cannot be undone.`)) {
            console.log("handleDeletePipeline: User cancelled deletion."); // Log 4
            return;
        }
    
        setApiError(null);
        console.log(`handleDeletePipeline: CONFIRMED - Proceeding to delete pipeline ID: ${selectedPipeline.id}, Name: "${selectedPipeline.name}", Owner: "${selectedPipeline.username}"`); // Log 5
    
        try {
            const response = await axios.delete(`/api/prompt-pipelines/${selectedPipeline.id}`, { withCredentials: true });
            
            console.log("handleDeletePipeline: Delete API call successful. Response status:", response.status, "Response data:", response.data); // Log 6
            alert(`Pipeline "${selectedPipeline.name}" deleted successfully!`);
            
            refreshPipelines();      // Refresh the list from the server
            clearPipelineEditor();   // Clear the editor and selection
            
        } catch (error) {
            console.error("handleDeletePipeline: Error during delete API call or subsequent actions.", error); // Log 7
            if (error.response) {
                console.error("handleDeletePipeline: Error response data:", error.response.data);
                console.error("handleDeletePipeline: Error response status:", error.response.status);
            }
            handleApiError(error, `deleting pipeline "${selectedPipeline.name}"`);
        }
    }, [selectedPipeline, refreshPipelines, handleApiError, clearPipelineEditor]);

    // --- File Upload Sub-Component (kept the same - no changes needed here) ---
    const FileUploadHandler = React.memo(({ prompts, handleApiError, promptsLoading }) => {
        // --- Hooks called at the top level ---
        const uploadSectionIdBase = useId();
        const processSectionIdBase = useId();

        const findPromptById = useCallback((promptsList, id) => {
             if (!Array.isArray(promptsList) || !id) return undefined;
             const targetId = String(id);
             return promptsList.find(prompt => String(prompt.id) === targetId);
         }, []);

        // --- States (FileUploadHandler specific) ---
        const [files, setFiles] = useState([]);
        const [isSelectingDirectory, setIsSelectingDirectory] = useState(false);
        const [directoryName, setDirectoryName] = useState('');
        const [uploadLoading, setUploadLoading] = useState(false);
        const [uploadSuccessMessage, setUploadSuccessMessage] = useState('');
        const [uploadErrorMessage, setUploadErrorMessage] = useState('');
        const [uploadedFileNames, setUploadedFileNames] = useState([]);
        const [selectedSummaryPromptUpload, setSelectedSummaryPromptUpload] = useState(null);
        const [selectedExtractionPromptUpload, setSelectedExtractionPromptUpload] = useState(null);
        const [processingFlag, setProcessingFlag] = useState(false);
        // ... (rest of FileUploadHandler state: processLoading, messages, allFiles, etc.)
        const [processLoading, setProcessLoading] = useState(false);
        const [processSuccessMessage, setProcessSuccessMessage] = useState('');
        const [processErrorMessage, setProcessErrorMessage] = useState('');
        const [allFiles, setAllFiles] = useState([]);
        const [selectedFilesForProcessing, setSelectedFilesForProcessing] = useState([]);
        const [searchTerm, setSearchTerm] = useState("");
        const [fetchFilesLoading, setFetchFilesLoading] = useState(false);
        const [allDirectories, setAllDirectories] = useState([]);
        const [fetchDirsLoading, setFetchDirsLoading] = useState(false);
        const [selectedSummaryPromptProcess, setSelectedSummaryPromptProcess] = useState(null);
        const [selectedExtractionPromptProcess, setSelectedExtractionPromptProcess] = useState(null);
        // Derive prompt lists safely
        const summaryPrompts = Array.isArray(prompts) ? prompts.filter(prompt => prompt.group === 'summary') : [];
        const extractionPrompts = Array.isArray(prompts) ? prompts.filter(prompt => prompt.group === 'extraction') : [];

        // --- API Fetch Utility (FileUploadHandler specific) ---
        const fetchData = useCallback(async (url, params = {}, setLoadingState, context) => {
            setLoadingState(true);
            // Clear relevant error messages when fetching
            if (context === 'directories' || context.includes('files')) { setProcessErrorMessage(''); }
             setUploadErrorMessage(''); // Always clear upload error on fetch

            try {
                const response = await axios.get(url, { params, withCredentials: true });
                // Parse based on expected endpoint structure
                if (url.includes('/api/directories')) {
                     return Array.isArray(response.data.directories) ? response.data.directories : [];
                } else if (url.includes('/api/all_files_sorted') || url.includes('/api/search_files')) {
                    // Determine the key for results based on the endpoint
                    const dataKey = url.includes('/api/search_files') ? 'results' : 'files';
                    return Array.isArray(response.data?.[dataKey]) ? response.data[dataKey] : [];
                }
                // Default fallback if URL doesn't match known patterns
                return response.data || [];
            } catch (error) {
                 handleApiError(error, `fetching ${context || 'data'}`);
                 return []; // Return empty array on error
            } finally {
                 setLoadingState(false);
            }
        }, [handleApiError]); // Use the passed-in handleApiError

        // --- Effects (FileUploadHandler specific) ---
        useEffect(() => {
            const loadInitialData = async () => {
                setFetchDirsLoading(true);
                const fetchedDirs = await fetchData('/api/directories', {}, () => {}, 'directories');
                setAllDirectories(fetchedDirs);
                setFetchDirsLoading(false);
                // Only fetch initial files if search term is empty
                if (!searchTerm.trim()) {
                    setFetchFilesLoading(true);
                    const fetchedFiles = await fetchData('/api/all_files_sorted', { limit: 50, offset: 0 }, () => {}, 'initial files');
                    setAllFiles(fetchedFiles);
                    setFetchFilesLoading(false);
                }
            };
            loadInitialData();
            // Dependency: fetchData (which depends on handleApiError)
        }, [fetchData, searchTerm]); // Re-run if fetchData changes or searchTerm clears

        // --- Handlers (FileUploadHandler specific - kept the same logic) ---
        const handleFileChange = useCallback((event) => {
            const selectedFilesArray = Array.from(event.target.files);
            setFiles(selectedFilesArray);
            setUploadedFileNames(selectedFilesArray.map(f => f.name));
            setUploadSuccessMessage(''); setUploadErrorMessage(''); // Clear messages on new selection
        }, []);

        const handleDirectoryInputChange = useCallback((event) => {
            setDirectoryName(event.target.value);
            setUploadErrorMessage(''); // Clear error on change
        }, []);

        const setDirectoryModeNew = useCallback(() => {
            setIsSelectingDirectory(false);
            setDirectoryName(''); // Reset directory name when toggling
            setUploadErrorMessage(''); // Clear potential errors
        }, []);

        const setDirectoryModeSelect = useCallback(() => {
            setIsSelectingDirectory(true);
            setDirectoryName(''); // Reset directory name when toggling
            setUploadErrorMessage(''); // Clear potential errors
        }, []);

        const handleSearch = useCallback(async () => {
            setSelectedFilesForProcessing([]); // Clear selection on new search
            setProcessSuccessMessage(''); setProcessErrorMessage(''); // Clear processing messages
            const currentSearchTerm = searchTerm.trim();
            setFetchFilesLoading(true);
            let results = [];
            if (currentSearchTerm) {
                results = await fetchData('/api/search_files', { searchTerm: currentSearchTerm }, () => {}, `files matching '${currentSearchTerm}'`);
            } else {
                // Fetch initial sorted list if search is cleared
                results = await fetchData('/api/all_files_sorted', { limit: 50, offset: 0 }, () => {}, 'initial files');
            }
            setAllFiles(results);
            setFetchFilesLoading(false);
        }, [searchTerm, fetchData]);

        const handleFileCheckboxChange = useCallback((file) => {
            setSelectedFilesForProcessing(prev =>
                prev.some(f => f.id === file.id)
                    ? prev.filter(f => f.id !== file.id)
                    : [...prev, file]
            );
        }, []);

        const handleSelectAllToggle = useCallback(() => {
            if (allFiles.length === 0) return;
            setSelectedFilesForProcessing(prev =>
                prev.length === allFiles.length ? [] : [...allFiles]
            );
        }, [allFiles]);

        const handleFileUpload = useCallback(async () => {
            // Input validation
            if (files.length === 0 || !directoryName.trim()) {
                setUploadErrorMessage('Please enter a directory name and select files.');
                return;
            }
            if (processingFlag && (!selectedSummaryPromptUpload || !selectedExtractionPromptUpload)) {
                setUploadErrorMessage('Please select both summary and extraction prompts when processing during upload.');
                return;
            }

            // Prepare form data
            const formData = new FormData();
            formData.append('directory', directoryName.trim());
            formData.append('customPromptSummaryID', selectedSummaryPromptUpload?.id || "");
            formData.append('customPromptExtractionID', selectedExtractionPromptUpload?.id || "");
            formData.append('processingFlag', processingFlag);
            // Give each file a unique name based on current time
            files.forEach(file => {
                const timestamp = Date.now();
                // Optionally, add a random number for extra uniqueness
                const uniqueName = `${timestamp}_${Math.floor(Math.random() * 10000)}_${file.name}`;
                const uniqueFile = new File([file], uniqueName, { type: file.type });
                formData.append('uploaded_files', uniqueFile);
            });

            // Excel Log Generation (Optional - kept as is)
            try {
                const logData = files.map(file => ({
                    filename: file.name, directory: directoryName.trim(), upload_time: new Date().toISOString(), size_bytes: file.size,
                    processed_on_upload: processingFlag,
                    summary_prompt_id: processingFlag && selectedSummaryPromptUpload ? selectedSummaryPromptUpload.id : 'N/A',
                    extraction_prompt_id: processingFlag && selectedExtractionPromptUpload ? selectedExtractionPromptUpload.id : 'N/A',
                }));
                const wb = XLSX.utils.book_new(); const ws = XLSX.utils.json_to_sheet(logData);
                XLSX.utils.book_append_sheet(wb, ws, 'Upload Log');
                const safeDir = directoryName.trim().replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'upload';
                const logFile = `${safeDir}_log_${Date.now()}.xlsx`;
                XLSX.writeFile(wb, logFile); // Triggers browser download
                console.log(`Generated and downloaded log: ${logFile}`);
            } catch (logErr) {
                 console.error("Excel log generation failed:", logErr);
                 setUploadErrorMessage("Could not generate Excel log (see console), continuing upload."); // Inform user but don't block
            }

            setUploadLoading(true);
            setUploadSuccessMessage(''); // Clear success from previous attempts
            // Keep potential log error message visible

            try {
                 // API call
                 const response = await axios.post('/api/upload-and-save', formData, {
                     headers: { 'Content-Type': 'multipart/form-data' },
                     withCredentials: true
                 });
                 setUploadSuccessMessage(response.data.message || 'Files uploaded successfully!');
                 setUploadErrorMessage(''); // Clear any previous error

                 // Reset upload form state on success
                 setFiles([]); setDirectoryName(''); setUploadedFileNames([]);
                setProcessingFlag(false); setSelectedSummaryPromptUpload(null); setSelectedExtractionPromptUpload(null);
                setIsSelectingDirectory(false);
                 // Reset file input visually
                 if (document.getElementById('fileInput')) document.getElementById('fileInput').value = null;

                 // Refresh directory list and file list (if no search term)
                 await fetchData('/api/directories', {}, setFetchDirsLoading, 'directories');
                 if (!searchTerm.trim()) {
                     const fetchedFiles = await fetchData('/api/all_files_sorted', { limit: 50, offset: 0 }, setFetchFilesLoading, 'files after upload');
                     setAllFiles(fetchedFiles);
                 }

            } catch (error) {
                 handleApiError(error, 'uploading files');
                 setUploadErrorMessage(error.response?.data?.error || error.message || 'Upload failed.');
                 setUploadSuccessMessage(''); // Clear success message on error
            } finally {
                 setUploadLoading(false);
            }
        }, [files, directoryName, processingFlag, selectedSummaryPromptUpload, selectedExtractionPromptUpload, handleApiError, fetchData, searchTerm, isSelectingDirectory]);

        const handleStartProcessing = useCallback(async () => {
            // Input validation
            if (selectedFilesForProcessing.length === 0) {
                setProcessErrorMessage('Please select at least one file to process.'); return;
            }
            if (!selectedSummaryPromptProcess || !selectedExtractionPromptProcess) {
                setProcessErrorMessage('Please select both summary and extraction prompts for processing.'); return;
            }

            // Prepare API request
            const apiUrl = '/api/process-files';
            const requestBody = {
                file_ids: selectedFilesForProcessing.map(f => f.id),
                summaryPromptID: selectedSummaryPromptProcess.id,
                extractionPromptID: selectedExtractionPromptProcess.id
            };
            const successMsg = `${selectedFilesForProcessing.length} file(s)`;
            const context = 'processing selected files';

            setProcessLoading(true); setProcessSuccessMessage(''); setProcessErrorMessage('');

            try {
                 // API call to trigger backend processing
                 const response = await axios.post(apiUrl, requestBody, { withCredentials: true });
                 setProcessSuccessMessage(`${successMsg} processing started: ${response.data.message || 'Request submitted.'}`);
                 setSelectedFilesForProcessing([]); // Clear selection on successful submission

            } catch (error) {
                 handleApiError(error, context);
                 // Display specific processing error
                 setProcessErrorMessage(error.response?.data?.error || error.message || 'Processing request failed.');
                 setProcessSuccessMessage(''); // Clear success message
            } finally {
                 setProcessLoading(false);
            }
        }, [selectedFilesForProcessing, selectedSummaryPromptProcess, selectedExtractionPromptProcess, handleApiError]);

        // --- Reusable Prompt Selection UI (FileUploadHandler specific - kept same) ---
        const renderPromptsSelection = useCallback((
            selectedSummary, onSummaryChange,
            selectedExtraction, onExtractionChange,
            isLoadingPrompts, baseId
        ) => {
            const summaryId = `summaryPrompt-${baseId}`;
            const extractionId = `extractionPrompt-${baseId}`;
            return (
                <div className="processing-settings">
                    <h4>Processing Prompts</h4>
                    {isLoadingPrompts ? ( <p>Loading prompts...</p> ) : (
                        <>
                            <div className="form-section">
                                <label htmlFor={summaryId}>Summary Prompt:</label>
                                <select id={summaryId} onChange={onSummaryChange} value={selectedSummary?.id || ''} disabled={!summaryPrompts.length}>
                                    <option value="">-- Select --</option>
                                    {summaryPrompts.map(prompt => ( <option key={prompt.id} value={prompt.id}>{prompt.prompt_name}</option> ))}
                                </select>
                                {summaryPrompts.length === 0 && <small>No prompts available.</small>}
                            </div>
                            <div className="form-section">
                                <label htmlFor={extractionId}>Extraction Prompt:</label>
                                <select id={extractionId} onChange={onExtractionChange} value={selectedExtraction?.id || ''} disabled={!extractionPrompts.length}>
                                    <option value="">-- Select --</option>
                                    {extractionPrompts.map(prompt => ( <option key={prompt.id} value={prompt.id}>{prompt.prompt_name}</option> ))}
                                </select>
                                {extractionPrompts.length === 0 && <small>No prompts available.</small>}
                            </div>
                        </>
                     )}
                </div>
            );
        }, [summaryPrompts, extractionPrompts]); // Dependencies for prompt lists

        // --- JSX Return for FileUploadHandler (kept the same structure) ---
        return (
            <div className="file-upload-handler config-page-content">
                {/* Upload Section */}
                <section className="upload-section">
                    <h3>Upload New Files</h3>

                    {/* Directory Input/Select Area */}
                    <div className="form-section directory-selection">
                        <label>Directory:</label>
                        <div className="directory-mode-toggle">
                            <button
                                onClick={setDirectoryModeNew}
                                disabled={uploadLoading || fetchDirsLoading}
                                className={`toggle-button ${!isSelectingDirectory ? 'active' : ''}`}
                                type="button"
                                aria-pressed={!isSelectingDirectory}
                            >
                                Enter New
                            </button>
                            <button
                                onClick={setDirectoryModeSelect}
                                disabled={uploadLoading || fetchDirsLoading || allDirectories.length === 0}
                                className={`toggle-button ${isSelectingDirectory ? 'active' : ''}`}
                                type="button"
                                aria-pressed={isSelectingDirectory}
                                title={allDirectories.length === 0 ? "No existing directories found" : "Select Existing Directory"}
                            >
                                Select Existing
                            </button>
                        </div>

                        {isSelectingDirectory ? (
                            // Select Existing Directory Dropdown
                            <select
                                id={`directorySelect-${uploadSectionIdBase}`}
                                value={directoryName}
                                onChange={handleDirectoryInputChange}
                                disabled={uploadLoading || fetchDirsLoading || allDirectories.length === 0}
                                required // Keep validation in handler
                            >
                                <option value="">-- Select Directory --</option>
                                {fetchDirsLoading ? (
                                    <option disabled>Loading directories...</option>
                                ) : (
                                    allDirectories.map((dir) => (
                                        <option key={dir} value={dir}>{dir}</option>
                                    ))
                                )}
                            </select>
                        ) : (
                            // Enter New Directory Input
                            <input
                                id={`directoryNameUpload-${uploadSectionIdBase}`}
                                type="text"
                                placeholder="Enter new directory name"
                                value={directoryName}
                                onChange={handleDirectoryInputChange}
                                required // Keep validation in handler
                                disabled={uploadLoading}
                            />
                        )}
                        {fetchDirsLoading && isSelectingDirectory && <small> Loading directories...</small>}
                        {!fetchDirsLoading && isSelectingDirectory && allDirectories.length === 0 && <small className="warning-message"> No existing directories found.</small>}
                    </div>

                    {/* File Input */}
                    <div className="form-section">
                        <label htmlFor="fileInput">Select Files:</label>
                        <input id="fileInput" type="file" multiple onChange={handleFileChange} required disabled={uploadLoading} />
                    </div>
                    {/* Processing Toggle */}
                    <div className="processing-toggle">
                        <input type="checkbox" id={`processingCheckbox-${uploadSectionIdBase}`} checked={processingFlag} onChange={(e) => setProcessingFlag(e.target.checked)} disabled={uploadLoading} />
                        <label htmlFor={`processingCheckbox-${uploadSectionIdBase}`}>Process during upload?</label>
                    </div>
                    {/* Conditional Prompt Selection */}
                    {processingFlag && renderPromptsSelection(
                        selectedSummaryPromptUpload, (e) => setSelectedSummaryPromptUpload(findPromptById(prompts, e.target.value)),
                        selectedExtractionPromptUpload, (e) => setSelectedExtractionPromptUpload(findPromptById(prompts, e.target.value)),
                        promptsLoading, uploadSectionIdBase
                    )}
                    {/* Upload Button */}
                    <div className="button-group">
                        <button onClick={handleFileUpload} disabled={uploadLoading || files.length === 0 || !directoryName.trim() || (processingFlag && (!selectedSummaryPromptUpload || !selectedExtractionPromptUpload))} className="primary">
                            {uploadLoading ? 'Uploading...' : 'Upload Files'}
                        </button>
                    </div>
                    {/* Status Messages */}
                    {uploadLoading && <div className="loading-message">Uploading...</div>}
                    {uploadSuccessMessage && <div className="success-message">{uploadSuccessMessage}</div>}
                    {uploadErrorMessage && <div className="error-message">{uploadErrorMessage}</div>}
                    {/* Selected Files Preview */}
                    {uploadedFileNames.length > 0 && !uploadLoading && (
                        <div className="uploaded-files-log">
                            <h4>Selected:</h4>
                            <ul> {uploadedFileNames.map((name, index) => ( <li key={index}>{name}</li> ))} </ul>
                        </div>
                    )}
                </section>

                {/* Process Existing Section */}
                <section className="process-section">
                    <h3>Process Existing Files</h3>
                    {/* Search */}
                    <div className="search-container form-section">
                        <input type="text" placeholder="Search files..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} disabled={fetchFilesLoading || processLoading} aria-label="Search existing files" />
                        <button onClick={handleSearch} disabled={fetchFilesLoading || processLoading || !searchTerm.trim()} className="secondary">Search</button>
                        <button onClick={() => { setSearchTerm(''); handleSearch(); }} disabled={fetchFilesLoading || processLoading} className="cancel" title="Clear Search & Reload All Files">Clear</button>
                    </div>
                    {fetchFilesLoading && <div className="loading-message compact">Searching files...</div>}

                    {/* File Selection List */}
                    <div className="selection-area">
                         <label>Select Files to Process:</label>
                         <button onClick={handleSelectAllToggle} className="select-all-button" disabled={allFiles.length === 0 || processLoading || fetchFilesLoading}>
                             {selectedFilesForProcessing.length === allFiles.length && allFiles.length > 0 ? 'Deselect All' : 'Select All'} ({selectedFilesForProcessing.length} / {allFiles.length})
                         </button>
                        <div className="file-list-container">
                             {allFiles.length > 0 ? (
                                // Sort files alphabetically by name for display
                                allFiles.slice().sort((a, b) => (a.file_name || '').localeCompare(b.file_name || '')).map(file => {
                                    const fileItemId = `file-checkbox-${processSectionIdBase}-${file.id}`;
                                    return ( <div key={file.id} className="file-list-item">
                                        <input type="checkbox" id={fileItemId} checked={selectedFilesForProcessing.some(sf => sf.id === file.id)} onChange={() => handleFileCheckboxChange(file)} disabled={processLoading || fetchFilesLoading}/>
                                        <label htmlFor={fileItemId}>{file.file_name} {file.directory ? `(${file.directory})` : ''}</label>
                                    </div> );
                                })
                             ) : ( !fetchFilesLoading && <div className="empty-list-message">No files found{searchTerm ? ` matching "${searchTerm}"` : ""}. Use search or check connection.</div> )}
                        </div>
                    </div>

                    {/* Conditional Prompt Selection for Processing */}
                    {selectedFilesForProcessing.length > 0 && renderPromptsSelection(
                        selectedSummaryPromptProcess, (e) => setSelectedSummaryPromptProcess(findPromptById(prompts, e.target.value)),
                        selectedExtractionPromptProcess, (e) => setSelectedExtractionPromptProcess(findPromptById(prompts, e.target.value)),
                        promptsLoading, processSectionIdBase
                    )}

                    {/* Process Button */}
                    <div className="button-group">
                        <button onClick={handleStartProcessing} disabled={processLoading || fetchFilesLoading || selectedFilesForProcessing.length === 0 || !selectedSummaryPromptProcess || !selectedExtractionPromptProcess} className="primary">
                            {processLoading ? 'Starting Processing...' : `Process ${selectedFilesForProcessing.length} Selected File(s)`}
                        </button>
                    </div>
                    {/* Status Messages for Processing */}
                     {processLoading && <div className="loading-message">Submitting processing request...</div>}
                    {processSuccessMessage && <div className="success-message">{processSuccessMessage}</div>}
                    {processErrorMessage && <div className="error-message">{processErrorMessage}</div>}
                </section>
            </div>
        );
    }); // End of FileUploadHandler


    // --- Sub-Components for Content Areas (Memoized - most kept same) ---

    // --- UPDATED: MainContent passes isSaving down ---
    const MainContent = React.memo(({ settings, providerModelMap, maxTokenValue, handleSettingChange, handleSliderChange, handleSaveSettings, isSaving }) => {
        // Calculate percentages for sliders (visual feedback)
        const tempPercent = ((settings.temperature || 0) / 2) * 100; // Assuming max temp is 2
        // Adjust token percentage calculation to avoid division by zero if maxTokenValue is 1
        const tokenPercent = maxTokenValue > 1 ? (((settings.maxTokens || 1) - 1) / (maxTokenValue - 1)) * 100 : 0;
        // Get available models for the selected provider
        const availableModels = settings.llmProvider ? providerModelMap[settings.llmProvider] || [] : [];

        return (
            <div className="main-settings-content config-page-content">
                <h3>Main LLM Settings</h3>
                <p>Configure your main Large Language Model (LLM) settings. Changes are saved to your user profile on the server.</p>
                <div className="settings-grid">
                    {/* Provider Selection */}
                    <div className="settings-label-cell"><label htmlFor="llmProvider">Provider:</label></div>
                    <div className="settings-control-cell">
                        <select id="llmProvider" value={settings.llmProvider} onChange={(e) => handleSettingChange('llmProvider', e.target.value)} disabled={isSaving}>
                            {Object.keys(providerModelMap).map(provider => (
                                <option key={provider} value={provider}>{provider}</option>
                            ))}
                        </select>
                    </div>
                    {/* Model Selection */}
                    <div className="settings-label-cell"><label htmlFor="llmModel">Model:</label></div>
                    <div className="settings-control-cell">
                        <select id="llmModel" value={settings.model} onChange={(e) => handleSettingChange('model', e.target.value)} disabled={isSaving || !settings.llmProvider || availableModels.length === 0}>
                            {!settings.llmProvider ? ( <option value="">Select provider first</option> ) :
                             availableModels.length > 0 ? (
                                availableModels.map(model => ( <option key={model.name} value={model.name}>{model.name}</option> ))
                             ) : ( <option value="">No models listed for provider</option> )}
                        </select>
                    </div>
                    {/* Endpoint URL Display (Read-only) */}
                    <div className="settings-label-cell"><label htmlFor="modelUrl">Endpoint URL:</label></div>
                    <div className="settings-control-cell">
                         <input id="modelUrl" type="text" value={settings.url || 'N/A'} readOnly title={settings.url || 'N/A'} disabled={isSaving}/>
                    </div>
                    {/* Temperature Slider */}
                    <div className="settings-label-cell"><label htmlFor="temperature">Temperature:</label></div>
                    <div className="settings-control-cell slider-group">
                        <input id="temperature" type="range" min="0" max="2" step="0.05" value={settings.temperature} onChange={(e) => handleSliderChange('temperature', e.target.value)} style={{ '--value-percent': `${tempPercent}%` }} disabled={isSaving} />
                        <span>{settings.temperature?.toFixed(2) ?? 'N/A'}</span>
                    </div>
                    {/* Max Tokens Slider */}
                    <div className="settings-label-cell"><label htmlFor="maxTokens">Max Tokens:</label></div>
                    <div className="settings-control-cell slider-group">
                        <input id="maxTokens" type="range" min="1" max={maxTokenValue || 1} step={maxTokenValue > 1000 ? 50 : 1} value={settings.maxTokens} onChange={(e) => handleSliderChange('maxTokens', e.target.value)} disabled={isSaving || !maxTokenValue || maxTokenValue <= 1} style={{ '--value-percent': `${tokenPercent}%` }} />
                        <span>{settings.maxTokens ?? 'N/A'}</span>
                        <small>(Model Max: {maxTokenValue ? maxTokenValue.toLocaleString() : 'N/A'})</small>
                    </div>
                    {/* Save Button */}
                    <div className="modal-button-group" style={{ gridColumn: '1 / -1' }}>
                        <button onClick={handleSaveSettings} className="primary" disabled={isSaving}>
                             {isSaving ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                </div>
                {/* Optional: Display saving indicator */}
                {/* {isSaving && <div className="loading-message compact" style={{marginTop: '10px'}}>Saving...</div>} */}
            </div>
        );
    });    

    // --- Prompt Management Sub-Component (kept the same) ---
    const PromptManagementContent = React.memo(({
        prompts,
        selectedPrompt,
        handleSelectPromptChange,
        handleUpdatePrompt,
        handleCreatePrompt,
        promptsLoading,
        clearSelectedPrompt,
        globalSettings,
        providerModelMap,
        handleDeletePrompt // <-- Destructure the new prop here
    }) => {
        const [internalPromptName, setInternalPromptName] = useState('');
        const [internalPromptText, setInternalPromptText] = useState('');
        const [internalPromptGroup, setInternalPromptGroup] = useState('');
    
        const [useCustomPromptSettings, setUseCustomPromptSettings] = useState(false);
        const [promptProvider, setPromptProvider] = useState(globalSettings.llmProvider || 'OpenAI');
        const [promptModel, setPromptModel] = useState(globalSettings.model || '');
        const [promptTemperature, setPromptTemperature] = useState(globalSettings.temperature || 0.5);
        const [promptMaxTokens, setPromptMaxTokens] = useState(globalSettings.maxTokens || 1000);
        const [promptModelMaxLimit, setPromptModelMaxLimit] = useState(16000);
    
        useEffect(() => {
            const currentProviderModels = providerModelMap[promptProvider] || [];
            const currentModelDetails = currentProviderModels.find(m => m.name === promptModel);
    
            if (currentModelDetails) {
                const modelMax = parseInt(currentModelDetails.model_max_tokens, 10);
                setPromptModelMaxLimit(isNaN(modelMax) || modelMax <= 0 ? 16000 : modelMax);
                setPromptMaxTokens(prev => Math.min(prev, isNaN(modelMax) || modelMax <= 0 ? 16000 : modelMax));
            } else if (currentProviderModels.length > 0) {
                // If current model is not found, but provider has models, default to first model
                // This handles cases where provider changes and previous model is invalid for new provider
                if (!currentProviderModels.find(m => m.name === promptModel)) {
                    setPromptModel(currentProviderModels[0].name);
                    const firstModelDetails = currentProviderModels[0];
                    const modelMax = parseInt(firstModelDetails.model_max_tokens, 10);
                    setPromptModelMaxLimit(isNaN(modelMax) || modelMax <= 0 ? 16000 : modelMax);
                    setPromptMaxTokens(prev => Math.min(prev, isNaN(modelMax) || modelMax <= 0 ? 16000 : modelMax));
                }
            } else {
                // No models for current provider, or provider invalid
                setPromptModel('');
                setPromptModelMaxLimit(16000);
            }
        }, [promptProvider, promptModel, providerModelMap]); // Removed promptMaxTokens dependency to avoid loop, it's set correctly inside.
    
        useEffect(() => {
            if (selectedPrompt) {
                setInternalPromptName(selectedPrompt.prompt_name || '');
                setInternalPromptText(selectedPrompt.prompt_text || '');
                setInternalPromptGroup(selectedPrompt.group || ''); 
    
                if (selectedPrompt.provider && selectedPrompt.model) {
                    setUseCustomPromptSettings(true);
                    setPromptProvider(selectedPrompt.provider);
                    setPromptModel(selectedPrompt.model);
                    
                    let tempToSet;
                    if (selectedPrompt.temperature !== null && selectedPrompt.temperature !== undefined) {
                        const parsedTemp = parseFloat(selectedPrompt.temperature);
                        tempToSet = !isNaN(parsedTemp) ? parsedTemp : (globalSettings.temperature || 0.5);
                    } else {
                        tempToSet = (globalSettings.temperature || 0.5);
                    }
                    setPromptTemperature(tempToSet);

                    let tokensToSet;
                    if (selectedPrompt.max_tokens !== null && selectedPrompt.max_tokens !== undefined) {
                        const parsedTokens = parseInt(selectedPrompt.max_tokens, 10);
                        tokensToSet = !isNaN(parsedTokens) ? parsedTokens : (globalSettings.maxTokens || 1000);
                    } else {
                        tokensToSet = (globalSettings.maxTokens || 1000);
                    }
                    setPromptMaxTokens(tokensToSet);
                    
                    const modelDetails = getModelDetails(selectedPrompt.provider, selectedPrompt.model, providerModelMap);
                    if (modelDetails) {
                        const modelMax = parseInt(modelDetails.model_max_tokens, 10);
                        setPromptModelMaxLimit(isNaN(modelMax) || modelMax <= 0 ? 16000 : modelMax);
                    } else {
                        setPromptModelMaxLimit(16000);
                    }
    
                } else { 
                    setUseCustomPromptSettings(false);
                    setPromptProvider(globalSettings.llmProvider || 'OpenAI');
                    const initialProvider = globalSettings.llmProvider || 'OpenAI';
                    const initialModels = providerModelMap[initialProvider] || [];
                    setPromptModel(globalSettings.model || (initialModels.length > 0 ? initialModels[0].name : ''));
                    setPromptTemperature(globalSettings.temperature || 0.5); 
                    setPromptMaxTokens(globalSettings.maxTokens || 1000); 
                    
                    const globalModelDetails = getModelDetails(globalSettings.llmProvider, globalSettings.model, providerModelMap);
                     if (globalModelDetails) {
                        const modelMax = parseInt(globalModelDetails.model_max_tokens, 10);
                        setPromptModelMaxLimit(isNaN(modelMax) || modelMax <= 0 ? 16000 : modelMax);
                    } else {
                         setPromptModelMaxLimit(16000);
                    }
                }
            } else { 
                setInternalPromptName('');
                setInternalPromptText('');
                setInternalPromptGroup('');
                setUseCustomPromptSettings(false);
                setPromptProvider(globalSettings.llmProvider || 'OpenAI');
                const initialProvider = globalSettings.llmProvider || 'OpenAI';
                const initialModels = providerModelMap[initialProvider] || [];
                setPromptModel(globalSettings.model || (initialModels.length > 0 ? initialModels[0].name : ''));
                setPromptTemperature(globalSettings.temperature || 0.5); 
                setPromptMaxTokens(globalSettings.maxTokens || 1000); 
                
                const initialModelDetails = initialModels.find(m => m.name === (globalSettings.model || (initialModels.length > 0 ? initialModels[0].name : '')));
                if (initialModelDetails) {
                    const modelMax = parseInt(initialModelDetails.model_max_tokens, 10);
                    setPromptModelMaxLimit(isNaN(modelMax) || modelMax <= 0 ? 16000 : modelMax);
                } else {
                    setPromptModelMaxLimit(16000);
                }
            }
        }, [selectedPrompt, globalSettings, providerModelMap]);
    
        const handlePromptLLMSettingChange = (key, value) => {
            if (key === 'provider') {
                setPromptProvider(value);
                const firstModelForProvider = providerModelMap[value]?.[0];
                if (firstModelForProvider) {
                    setPromptModel(firstModelForProvider.name);
                    // max limit and current tokens will be updated by the useEffect for promptProvider/promptModel
                } else {
                    setPromptModel('');
                }
            } else if (key === 'model') {
                setPromptModel(value);
                // max limit and current tokens will be updated by the useEffect for promptProvider/promptModel
            } else if (key === 'temperature') {
                const numericValue = parseFloat(value);
                if (!isNaN(numericValue)) {
                    setPromptTemperature(Math.max(0, Math.min(2, numericValue)));
                }
            } else if (key === 'maxTokens') {
                const numericValue = parseInt(value, 10);
                if (!isNaN(numericValue)) {
                    setPromptMaxTokens(Math.max(1, Math.min(numericValue, promptModelMaxLimit)));
                }
            }
        };
        
        const onClearSelectedPrompt = () => {
            clearSelectedPrompt(); 
            setInternalPromptName('');
            setInternalPromptText('');
            setInternalPromptGroup('');
            setUseCustomPromptSettings(false);
            setPromptProvider(globalSettings.llmProvider || 'OpenAI');
            const resetProvider = globalSettings.llmProvider || 'OpenAI';
            const resetModels = providerModelMap[resetProvider] || [];
            const resetModelName = globalSettings.model || (resetModels.length > 0 ? resetModels[0].name : '');
            setPromptModel(resetModelName);
            setPromptTemperature(globalSettings.temperature || 0.5);
            setPromptMaxTokens(globalSettings.maxTokens || 1000);
            const resetModelDetails = resetModels.find(m => m.name === resetModelName);
            if (resetModelDetails) {
                const modelMax = parseInt(resetModelDetails.model_max_tokens, 10);
                setPromptModelMaxLimit(isNaN(modelMax) || modelMax <= 0 ? 16000 : modelMax);
            } else {
                setPromptModelMaxLimit(16000);
            }
        };
    
        const prepareLLMSettingsPayload = () => {
            if (!useCustomPromptSettings) {
                return { provider: null, model: null, temperature: null, max_tokens: null };
            }
            if (!promptProvider || !promptModel) {
                alert("If using custom LLM settings for the prompt, Provider and Model must be selected.");
                return null; 
            }
            return {
                provider: promptProvider,
                model: promptModel,
                temperature: promptTemperature,
                max_tokens: promptMaxTokens,
            };
        };
    
        const canSubmit = internalPromptName.trim() && internalPromptText.trim() &&
                          (!useCustomPromptSettings || (promptProvider && promptModel));
    
        const availablePromptModels = promptProvider ? providerModelMap[promptProvider] || [] : [];
        const promptTempPercent = ((promptTemperature || 0) / 2) * 100;
        const promptTokenPercent = promptModelMaxLimit > 1 ? (((promptMaxTokens || 1) - 1) / (promptModelMaxLimit - 1)) * 100 : 0;

        // Determine if the selected prompt is a system prompt
        const isSystemPrompt = selectedPrompt && (selectedPrompt.username === null || selectedPrompt.username === '');
    
        return (
            <div className="prompts-content-area config-page-content">
                <h3>Manage Prompts</h3>
                <p>View, edit, or create new prompts. Optionally, assign specific LLM settings to a prompt.</p>
                {promptsLoading && <div className="loading-message compact">Loading prompts...</div>}
                <div className="form-section">
                    <label htmlFor="promptSelect">Select Prompt to View/Edit:</label>
                    <select id="promptSelect" onChange={handleSelectPromptChange} value={selectedPrompt?.id || ''} disabled={promptsLoading || prompts.length === 0}>
                        <option value="">-- Select --</option>
                        {prompts.slice().sort((a, b) => (a.prompt_name || '').localeCompare(b.prompt_name || '')).map(prompt => (
                            <option key={prompt.id} value={prompt.id}>{prompt.prompt_name} {prompt.username === null || prompt.username === '' ? '(System)' : `(${prompt.username})`}</option>
                        ))}
                    </select>
                    {prompts.length === 0 && !promptsLoading && <small>No prompts found.</small>}
                </div>
    
                <div className="prompt-details-section">
                    <h4>{selectedPrompt ? (isSystemPrompt ? `View System Prompt: ${selectedPrompt.prompt_name}` : `Edit Prompt: ${selectedPrompt.prompt_name}`) : 'Create New Prompt'}</h4>
                    <div className="form-section">
                        <label htmlFor="promptName">Name:</label>
                        <input id="promptName" type="text" placeholder="Enter prompt name" value={internalPromptName} onChange={(e) => setInternalPromptName(e.target.value)} />
                    </div>
                    <div className="form-section">
                        <label htmlFor="promptText">Text:</label>
                        <textarea id="promptText" placeholder="Enter prompt text" value={internalPromptText} onChange={(e) => setInternalPromptText(e.target.value)} rows="8" />
                    </div>
                    <div className="form-section">
                        <label htmlFor="promptGroup">Group (optional, e.g., summary, extraction):</label>
                        <input id="promptGroup" type="text" placeholder="Enter group name" value={internalPromptGroup} onChange={(e) => setInternalPromptGroup(e.target.value)} />
                    </div>
    
                    <div className="form-section prompt-llm-settings-section" style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px dashed #ccc' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                            <input
                                type="checkbox"
                                id="useCustomPromptSettings"
                                checked={useCustomPromptSettings}
                                onChange={(e) => setUseCustomPromptSettings(e.target.checked)}
                                style={{ marginRight: '10px', width: 'auto', height: 'auto' }}
                            />
                            <label htmlFor="useCustomPromptSettings" style={{ marginBottom: '0', fontWeight: 'normal' }}>
                                Use custom LLM settings for this prompt
                            </label>
                        </div>
    
                        {useCustomPromptSettings && (
                            <div className="settings-grid" style={{ opacity: useCustomPromptSettings ? 1 : 0.5, pointerEvents: useCustomPromptSettings ? 'auto' : 'none' }}>
                                <div className="settings-label-cell"><label htmlFor="promptProvider">Provider:</label></div>
                                <div className="settings-control-cell">
                                    <select id="promptProvider" value={promptProvider} onChange={(e) => handlePromptLLMSettingChange('provider', e.target.value)}>
                                        {Object.keys(providerModelMap).map(pKey => (
                                            <option key={pKey} value={pKey}>{pKey}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="settings-label-cell"><label htmlFor="promptModel">Model:</label></div>
                                <div className="settings-control-cell">
                                    <select id="promptModel" value={promptModel} onChange={(e) => handlePromptLLMSettingChange('model', e.target.value)} disabled={!promptProvider || availablePromptModels.length === 0}>
                                        {!promptProvider ? ( <option value="">Select provider first</option> ) :
                                        availablePromptModels.length > 0 ? (
                                            availablePromptModels.map(model => ( <option key={model.name} value={model.name}>{model.name}</option> ))
                                        ) : ( <option value="">No models for provider</option> )}
                                    </select>
                                </div>
                                <div className="settings-label-cell"><label htmlFor="promptTemperature">Temperature:</label></div>
                                <div className="settings-control-cell slider-group">
                                    <input id="promptTemperature" type="range" min="0" max="2" step="0.05" value={promptTemperature} onChange={(e) => handlePromptLLMSettingChange('temperature', e.target.value)} style={{ '--value-percent': `${promptTempPercent}%` }}/>
                                    <span>{promptTemperature?.toFixed(2) ?? 'N/A'}</span>
                                </div>
                                <div className="settings-label-cell"><label htmlFor="promptMaxTokens">Max Tokens:</label></div>
                                <div className="settings-control-cell slider-group">
                                    <input id="promptMaxTokens" type="range" min="1" max={promptModelMaxLimit || 1} step={promptModelMaxLimit > 1000 ? 50 : 1} value={promptMaxTokens} onChange={(e) => handlePromptLLMSettingChange('maxTokens', e.target.value)} disabled={!promptModelMaxLimit || promptModelMaxLimit <= 1} style={{ '--value-percent': `${promptTokenPercent}%` }} />
                                    <span>{promptMaxTokens ?? 'N/A'}</span>
                                    <small>(Model Max: {promptModelMaxLimit ? promptModelMaxLimit.toLocaleString() : 'N/A'})</small>
                                </div>
                            </div>
                        )}
                    </div>
    
                    <div className="modal-button-group" style={{ marginTop: '20px' }}>
                        {/* Update Prompt Button - Conditionally Rendered */}
                        {selectedPrompt && !isSystemPrompt && (
                            <button
                                onClick={() => {
                                    const llmPayload = prepareLLMSettingsPayload();
                                    if (llmPayload) {
                                        handleUpdatePrompt(selectedPrompt.id, internalPromptName, internalPromptText, internalPromptGroup, llmPayload);
                                    }
                                }}
                                disabled={!canSubmit}
                                className="primary"
                            >
                                Update Prompt
                            </button>
                        )}
                        {/* Save as New Prompt Button - Always available if form is valid */}
                        <button
                            onClick={() => {
                                const llmPayload = prepareLLMSettingsPayload();
                                if (llmPayload) {
                                    handleCreatePrompt(internalPromptName, internalPromptText, internalPromptGroup, llmPayload);
                                }
                            }}
                            disabled={!canSubmit}
                            className="primary"
                        >
                            Save as New Prompt
                        </button>
                        
                        {/* Delete Prompt Button - Conditionally Rendered */}
                        {selectedPrompt && !isSystemPrompt && (
                            <button
                                onClick={handleDeletePrompt}
                                className="danger"
                                title={`Delete prompt: ${selectedPrompt?.prompt_name}`}
                            >
                                Delete Prompt
                            </button>
                        )}

                        {/* Clear / New Button */}
                        {(selectedPrompt || internalPromptName || internalPromptText || useCustomPromptSettings) && (
                            <button onClick={onClearSelectedPrompt} className="cancel">Clear / New</button>
                        )}
                    </div>
                </div>
            </div>
        );
    });
    
    // --- Pipeline Sub-Component (kept the same) ---
    const PipelineContent = React.memo(({
        selectedPipeline,
        pipelines = [],
        prompts = [],
        pipelinePrompts = [],
        handleSelectPipelineChange,
        selectedPrompt,
        handleSelectPromptChange,
        handleAddToPipeline,
        handleRemoveFromPipeline,
        handleSavePipeline,     // For "Save New Pipeline" (from scratch) AND "Save as New Copy"
        handleUpdatePipeline,   // For "Update My Pipeline" (user's own existing pipeline)
        handleDeletePipeline,   // For "Delete My Pipeline" - THIS IS THE PROP
        promptsLoading,
        pipelinesLoading,
        clearPipelineEditor
    }) => {
        const [internalPipelineName, setInternalPipelineName] = useState('');
    
        useEffect(() => {
            setInternalPipelineName(selectedPipeline?.name || '');
        }, [selectedPipeline]);
    
        // **DEBUGGING LOG 1: Monitor selectedPipeline in PipelineContent**
        useEffect(() => {
            console.log("PipelineContent: selectedPipeline state updated to:", selectedPipeline);
            if (selectedPipeline) {
                console.log("PipelineContent: selectedPipeline.username is:", selectedPipeline.username);
            }
        }, [selectedPipeline]);
    
        const currentPipelinePrompts = pipelinePrompts || [];
        const promptIdsForSave = currentPipelinePrompts.map(p => p.id);
        const canSubmit = internalPipelineName.trim() && promptIdsForSave.length > 0;
    
        const isUserOwnedPipelineSelected = useMemo(() => {
            const isOwned = selectedPipeline && 
                            selectedPipeline.username !== null && 
                            selectedPipeline.username !== '';
            // **DEBUGGING LOG 2: Monitor isUserOwnedPipelineSelected calculation**
            console.log(`PipelineContent: isUserOwnedPipelineSelected calculated: ${isOwned}. Based on selectedPipeline.username: '${selectedPipeline?.username}'`);
            return isOwned;
        }, [selectedPipeline]);
    
        const displayablePipelines = pipelines || [];
        const displayablePrompts = prompts || [];
    
        return (
            <div className="pipeline-content-area config-page-content">
                <h3>Manage Prompt Pipelines</h3>
                <p>
                    Create new pipelines or edit your existing ones. 
                    You can load any pipeline (system or your own) into the editor, modify it, 
                    and then use "Save as New Copy" to create a new pipeline under your account.
                </p>
    
                {/* ... (select pipeline dropdown, pipeline name input, add prompt section - remain the same) ... */}
                {/* <div className="form-section">
                    <label htmlFor="pipelineSelectDropdown">Select Pipeline to View/Edit:</label>
                    <select
                        id="pipelineSelectDropdown"
                        onChange={handleSelectPipelineChange}
                        value={selectedPipeline?.id || ''}
                        disabled={pipelinesLoading || displayablePipelines.length === 0}
                    >
                        <option value="">-- Select Pipeline --</option>
                        {pipelinesLoading && <option disabled>Loading pipelines...</option>}
                        {displayablePipelines.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(p => (
                            <option key={p.id} value={p.id}>
                                {p.name} {p.username === null || p.username === '' ? '(System)' : (p.username ? `(${p.username})` : '(User)')}
                            </option>
                        ))}
                    </select>
                    {displayablePipelines.length === 0 && !pipelinesLoading && <small>No pipelines found or available.</small>}
                </div> */}
    
                <h4>
                    {selectedPipeline 
                        ? `Editor (Loaded: ${selectedPipeline.name}${selectedPipeline.username === null || selectedPipeline.username === '' ? ' [System]' : ''})`
                        : 'Create New Pipeline'}
                </h4>
    
                <div className="form-section">
                    <label htmlFor="pipelineNameInput">Pipeline Name (for save/copy):</label>
                    <input
                        id="pipelineNameInput"
                        type="text"
                        placeholder="Enter pipeline name"
                        value={internalPipelineName}
                        onChange={(e) => setInternalPipelineName(e.target.value)}
                    />
                </div>
    
                <div className="form-section">
                    <label htmlFor="pipelinePromptSelect">Add Prompt to Sequence:</label>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <select
                            id="pipelinePromptSelect"
                            onChange={handleSelectPromptChange}
                            value={selectedPrompt?.id || ''}
                            style={{ flexGrow: 1 }}
                            disabled={promptsLoading || displayablePrompts.length === 0}
                        >
                            <option value="">-- Select a prompt --</option>
                            {promptsLoading && <option value="" disabled>Loading prompts...</option>}
                            {displayablePrompts.slice().sort((a, b) => (a.prompt_name || '').localeCompare(b.prompt_name || '')).map(prompt => (
                                <option key={prompt.id} value={prompt.id}>
                                    {prompt.prompt_name} {prompt.username === null || prompt.username === '' ? '(System)' : (prompt.username ? '' : '')}
                                </option>
                            ))}
                        </select>
                        <button onClick={handleAddToPipeline} className="secondary" style={{ flexShrink: 0 }} disabled={!selectedPrompt}>Add</button>
                    </div>
                    {displayablePrompts.length === 0 && !promptsLoading && <small>No prompts available to add.</small>}
                </div>
    
                <h4>Current Pipeline Sequence (Order Matters)</h4>
                <ul className="pipeline-prompt-list">
                    {currentPipelinePrompts.map((prompt, index) => (
                        <li key={`${prompt.id}-${index}`} className="pipeline-prompt-item" data-index={index + 1}>
                            <span>{prompt.prompt_name} {prompt.username === null || prompt.username === '' ? '(System)' : (prompt.username ? '' : '')}</span>
                            <button onClick={() => handleRemoveFromPipeline(prompt)} className="remove-button" title="Remove">X</button>
                        </li>
                    ))}
                    {currentPipelinePrompts.length === 0 && <li className="empty-pipeline">No prompts added.</li>}
                </ul>
                {/* End of unchanged JSX parts */}
    
                <div className="modal-button-group">
                    {/* Case 1: Editor is for a brand new pipeline (no pipeline loaded) */}
                    {!selectedPipeline && (
                        <button
                            onClick={() => {
                                console.log("PipelineContent: 'Save New Pipeline' button clicked.");
                                handleSavePipeline(internalPipelineName, promptIdsForSave);
                            }}
                            disabled={!canSubmit}
                            className="primary"
                            title="Save the current editor content as a new pipeline for yourself."
                        >
                            Save New Pipeline
                        </button>
                    )}
    
                    {/* Case 2: A user-owned pipeline is loaded - Show "Update My Pipeline" */}
                    {selectedPipeline && isUserOwnedPipelineSelected && (
                        <button
                            onClick={() => {
                                console.log("PipelineContent: 'Update My Pipeline' button clicked.");
                                handleUpdatePipeline(selectedPipeline.id, internalPipelineName, promptIdsForSave);
                            }}
                            disabled={!canSubmit}
                            className="primary"
                            title="Update your existing pipeline with the current editor content."
                        >
                            Update My Pipeline
                        </button>
                    )}
                    
                    {/* "Save as New Copy" - available if any pipeline is loaded (system or user's own) */}
                    {selectedPipeline && (
                        <button
                            onClick={() => {
                                console.log("PipelineContent: 'Save as New Copy' button clicked.");
                                handleSavePipeline(internalPipelineName, promptIdsForSave);
                            }}
                            disabled={!canSubmit}
                            className="secondary" 
                            title="Create a new pipeline for yourself using the current editor content, regardless of the loaded pipeline's origin."
                        >
                            Save as New Copy
                        </button>
                    )}
                    
                    {/* Delete button for USER'S OWN pipelines */}
                    {/* **DEBUGGING LOG 3: Check button rendering condition** */}
                    {console.log("PipelineContent: Rendering Delete button? Condition selectedPipeline && isUserOwnedPipelineSelected:", !!(selectedPipeline && isUserOwnedPipelineSelected))}
                    {selectedPipeline && isUserOwnedPipelineSelected && (
                        <button
                            onClick={() => {
                                // **DEBUGGING LOG 4: Direct onClick console log**
                                console.log("PipelineContent: DELETE BUTTON CLICKED! Calling handleDeletePipeline prop. Selected Pipeline ID:", selectedPipeline?.id);
                                handleDeletePipeline(); // This calls the prop from SettingsPage.js
                            }}
                            className="danger"
                            title={`Delete pipeline: ${selectedPipeline.name}`}
                        >
                            Delete My Pipeline
                        </button>
                    )}
    
                    {/* Clear / New Button */}
                    {(selectedPipeline || internalPipelineName.trim() || currentPipelinePrompts.length > 0) && (
                        <button onClick={clearPipelineEditor} className="cancel">Clear Editor / New</button>
                    )}
                </div>
            </div>
        );
    });

    // --- Other Settings Sub-Component (kept the same) ---
    const OtherSettingsContent = React.memo(({ currentTheme, setCurrentTheme, handleApiError }) => {
        const handleThemeChange = async (newTheme) => {
            // Do nothing if the theme is already set to the new value
            if (currentTheme === newTheme) return;
    
            const previousTheme = currentTheme;
            // Optimistically update the UI for a responsive feel
            setCurrentTheme(newTheme); 
    
            try {
                // Persist the change to the backend
                await axios.post('/api/user/theme', 
                    { theme: newTheme }, 
                    { withCredentials: true }
                );
            } catch (error) {
                // If the API call fails, revert the theme and show an error
                setCurrentTheme(previousTheme); 
                handleApiError(error, 'saving theme preference'); 
            }
        };
    
        return (
            <div className="other-settings-content config-page-content">
                <h3>Other Settings</h3>
                <p>Application-wide preferences and settings.</p>
                
                <h4>Appearance</h4>
                <div className="form-section">
                    <label>Theme</label>
                    <div className="theme-toggle-container">
                        <button 
                            className={`theme-button ${currentTheme === 'LIGHT' ? 'active' : ''}`}
                            onClick={() => handleThemeChange('LIGHT')}
                            aria-pressed={currentTheme === 'LIGHT'}
                        >
                            Light
                        </button>
                        <button 
                            className={`theme-button ${currentTheme === 'DARK' ? 'active' : ''}`}
                            onClick={() => handleThemeChange('DARK')}
                            aria-pressed={currentTheme === 'DARK'}
                        >
                            Dark
                        </button>
                    </div>
                </div>
            </div>
        );
    });


    // --- Main Component Return ---
    return (
        <div className="settings-page-container">
            <SettingsSidebar
                activeTab={activeTab} setActiveTab={setActiveTab}
                handleSelectPipeline={handleSelectPipelineChange} // Pass handler to load pipeline
                pipelines={pipelines} // Pass list of pipelines
                selectedPipeline={selectedPipeline} // Pass currently selected pipeline
                isLoading={pipelinesLoading} // Pass loading state for pipeline list
            />
            <div className="settings-content-area">
                 {/* Display API errors */}
                 {apiError && (
                     <div className="error-message" style={{marginBottom: '15px'}} onClick={() => setApiError(null)} title="Click to dismiss">
                         {apiError}
                     </div>
                 )}
                 {/* Render content based on active tab */}
                {activeTab === 'Main' && (
                     <MainContent
                         settings={settings}
                         providerModelMap={providerModelMap}
                         maxTokenValue={maxTokenValue}
                         handleSettingChange={handleSettingChange}
                         handleSliderChange={handleSliderChange}
                         handleSaveSettings={handleSaveSettings}
                         isSaving={isSaving} // Pass saving state
                     />
                )}
                {activeTab === 'Prompts' && (
                    <PromptManagementContent
                        prompts={prompts}
                        selectedPrompt={selectedPrompt}
                        handleSelectPromptChange={handleSelectPromptChange}
                        handleUpdatePrompt={handleUpdatePrompt}
                        handleCreatePrompt={handleCreatePrompt}
                        promptsLoading={promptsLoading}
                        clearSelectedPrompt={clearSelectedPrompt}
                        globalSettings={settings} 
                        providerModelMap={providerModelMap}
                        handleDeletePrompt={handleDeletePrompt} // <-- Add this new prop
                    />
                )}
                {activeTab === 'Prompt Pipelines' && (
                     <PipelineContent
                         selectedPipeline={selectedPipeline}
                         prompts={prompts} // Pass full prompts list
                         pipelinePrompts={pipelinePrompts} // Pass current editor's list of prompt objects
                         handleSelectPipelineChange={handleSelectPipelineChange} // For selecting a pipeline from dropdown
                         selectedPrompt={selectedPrompt} // Current prompt selected in the "add prompt" dropdown
                         handleSelectPromptChange={handleSelectPromptChange} // Handler for "add prompt" dropdown
                         handleAddToPipeline={handleAddToPipeline}
                         handleRemoveFromPipeline={handleRemoveFromPipeline}
                         handleSavePipeline={handleSavePipeline} // For new or "save as copy"
                         handleUpdatePipeline={handleUpdatePipeline} // For updating existing user-owned
                         // !!! FIX: Add the missing prop here !!!
                         handleDeletePipeline={handleDeletePipeline} // For deleting existing user-owned
                         promptsLoading={promptsLoading}
                         pipelinesLoading={pipelinesLoading}
                         clearPipelineEditor={clearPipelineEditor}
                     />
                 )}
                {activeTab === 'Data Upload' && (
                     <FileUploadHandler
                         prompts={prompts} // Pass prompts list for selection
                         handleApiError={handleApiError} // Pass error handler
                         promptsLoading={promptsLoading} // Pass loading state
                     />
                 )}
                {activeTab === 'Other Settings' && 
                    <OtherSettingsContent 
                        currentTheme={theme} 
                        setCurrentTheme={setTheme} 
                        handleApiError={handleApiError}
                    />
                }
            </div>
        </div>
     );
};

export default SettingsPage;

